import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Grid, Layout } from "antd";
import "./dashboardLayout.scss";
import { Outlet } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../redux/store";
import { getMe } from "../../redux/Slices/AuthSlice";
import { getSubscriptions } from "../../redux/Slices/BillingSlice";
import { UploadProgress } from "../../Components/UploadProgress/UploadProgress";
import RefreshErrorBoundary from "../../Components/ErrorBoundaries/RefreshErrorBoundary";
import DunningBanner from "../../Components/DunningBanner/DunningBanner";
import { PageSkeleton } from "../../Components/PageSkeleton/PageSkeleton";
import { Sidebar } from "./Sidebar";
import { Header } from "./Header";
import { MobileNav } from "./MobileNav";
import { ApiErrorState } from "../../Components/ApiErrorState/ApiErrorState";
import { useSocketEvent } from "../../hooks/useSocketEvent";
import { usePaymentActionEvents } from "../../hooks/usePaymentActionEvents";
import { getSocket } from "../../helpers/socket";

const { Content } = Layout;

const DashboardLayout: React.FC = () => {
  const dispatch = useAppDispatch();
  const breakpoint = Grid.useBreakpoint();
  const { t } = useTranslation("common");
  const { user, restoreState } = useAppSelector((state) => state.auth);

  useEffect(() => {
    if (restoreState === "authenticated") getSocket();
  }, [restoreState]);
  useSocketEvent("account_state_changed", () => {
    dispatch(getSubscriptions());
  });
  usePaymentActionEvents();

  // Subscription polling -- 60s (time-sensitive for dunning/gating) [PERF-01 D-02]
  useEffect(() => {
    dispatch(getSubscriptions());
    const interval = setInterval(() => {
      dispatch(getSubscriptions());
    }, 60 * 1000);
    return () => clearInterval(interval);
  }, [dispatch]);

  // User polling -- 3 minutes (rarely changes mid-session) [PERF-01 D-03]
  useEffect(() => {
    const loadUser = async () => {
      const result = await dispatch(getMe()).unwrap();
      if (result.state === "authenticated") getSocket();
    };
    loadUser();
    const interval = setInterval(() => {
      dispatch(getMe());
    }, 3 * 60 * 1000);
    return () => clearInterval(interval);
  }, [dispatch]);

  if (restoreState === "retryable_error") {
    return (
      <ApiErrorState
        title={t("apiError.restoreTitle")}
        description={t("apiError.restoreDescription")}
        onRetry={() => dispatch(getMe())}
      />
    );
  }
  if (!user || restoreState === "unauthorized") return <PageSkeleton />;

  return (
    <Layout className="dashboardLayout">
      <UploadProgress />
      <Layout hasSider={breakpoint.lg}>
        {breakpoint.lg ? <Sidebar /> : null}
        <Content className="content">
          {breakpoint.lg ? <Header /> : <MobileNav />}
          <DunningBanner />
          <RefreshErrorBoundary>
            <Outlet />
          </RefreshErrorBoundary>
        </Content>
      </Layout>
    </Layout>
  );
};

export default DashboardLayout;
